/* **How one survey answer is drawn**, wherever it is drawn: a matrix cell, a report line, a column
 * of a comparison. One stylesheet, because the three surfaces print the same eight kinds of answer
 * and three near-copies differing by two pixels is the duplication the styling rule exists to stop.
 *
 * ── The geometry that VARIES is a custom property, not a copy of the block ───────────────────
 * A progress photo is 58×76 in the matrix, 100×132 in the report and 118×156 in the comparison; a
 * clip is 118×66, 316×178 and 236×132. Those are the only differences, so the consumer states them
 * and everything else is here — the `scrollbar.module.css` idiom:
 *
 *   <PhotoStrip className={styles.matrixPhotos} />
 *   .panePhotos { --survey-photo-w: var(--sz-survey-photo-lg-w); --survey-photo-h: var(--sz-survey-photo-lg-h); }
 *
 * There is no fallback on either, deliberately, and for `scrollbar`'s own reason: a default written
 * here would be a declaration that a consumer's own could lose to depending on which chunk the
 * build emitted last.
 *
 * ── The accent is NOT in this file ───────────────────────────────────────────────────────────
 * Every reading is neutral. `--good-ink` and `--warn-ink` appear on a delta and nowhere else, and
 * only on a row whose direction the domain agrees on — see `MEASUREMENT_FIELDS`. Ember marks an act
 * and a reading is not one. */

/* ── a number, its unit and its change ──────────────────────────────────────── */

.reading {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: baseline;
  min-width: 0;
}

.figure {
  font-size: var(--fs-body);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--text);
  letter-spacing: var(--ls-section);
  font-variant-numeric: tabular-nums;
}

/* `--text-2`, not `--text-3`: the unit is what the figure beside it MEANS, and „74,6" without „cm"
 * is not a reading. `--text-3` measured 3,01:1 on the card in Flame and carries no text that has to
 * be read. */
.unit {
  margin-inline-start: var(--space-2px);
  font-size: var(--fs-tag);
  font-weight: var(--fw-medium);
  line-height: var(--lh-none);
  color: var(--text-2);
}

/* A rating that prints its figure beside its dashes — the report and the comparison. */
.rating {
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
}

.delta {
  font-size: var(--fs-micro-label);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The two verdict inks — THE pair, worn by every signed change in this tab: the matrix cell's delta,
 * the chart head's, the report's „vs poprzednia" line, its rail's sparkline and the comparison's
 * change column. There were three copies of them under three sets of names.
 *
 * `flat` is absent on purpose: a change with no agreed direction, and a change of zero, both keep
 * their surface's neutral ink — colouring a zero would claim a win nobody had.
 *
 * Written `.good.good` because four of those five surfaces declare their own neutral `color` from a
 * DIFFERENT stylesheet, and a single class against a single class is settled by CHUNK ORDER, which
 * differs between the dev server and the build — the trap `emberButton.module.css` records at
 * length. Repeating the one class name is the only form specificity can decide here. */
.good.good {
  color: var(--good-ink);
}

.warn.warn {
  color: var(--warn-ink);
}

/* ── a 1–5 rating, drawn as five dashes ────────────────────────────────────── */

.scale {
  display: inline-flex;
  gap: var(--space-3px);
  align-items: center;
}

.step {
  flex: none;
  width: var(--sz-survey-step-w);
  height: var(--sz-survey-step-h);
  background: var(--text);
  border-radius: var(--radius-hair);
}

.stepOff {
  box-sizing: border-box;
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border-2);
}

/* ── yes / no ───────────────────────────────────────────────────────────────── */

.mark {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-survey-mark);
  height: var(--sz-survey-mark);
  border-radius: var(--r-circle);
}

.markYes {
  color: var(--good-ink);
  background: var(--good-soft);
}

.markNo {
  color: var(--text-2);
  background: var(--bg-chip);
}

/* ── nothing was answered ───────────────────────────────────────────────────── */

.blank {
  display: inline-grid;
  place-items: center;
  color: var(--text-2);
}

/* The question was not on this occurrence's form at all. Dimmer than a blank, because "nobody was
 * asked" is a quieter fact than "asked and not answered" — and the row's own dashed plate says it
 * in words above these. */
.absent {
  opacity: 0.35;
}

/* ── words ──────────────────────────────────────────────────────────────────── */

/* IN FULL, and there is no „pokaż więcej". Owner, 16 September 2026: the report clipped a client's
 * answer at four lines with nothing to open it with, so the one thing on the tab she wrote herself
 * was the one thing the trainer could not read. What keeps it readable is the MEASURE — the caller's
 * box is capped at `--w-survey-measure` — and not a clamp. `text-wrap: pretty` so a paragraph does
 * not end on one word.
 *
 * ── The INK is the surface's, through a custom property ──────────────────────────────────────
 * The geometry idiom in this file's header, applied once to a colour. `--text-2` is right in a
 * matrix cell and in a comparison column, where a written answer stands beside the numbers it is
 * being read against; it is wrong in the report's ODPOWIEDZI band, which is the one band a trainer
 * genuinely READS and where `.answerValue` already sets primary ink for every other kind of answer.
 * Owner, 17 September 2026. The fallback is the default rather than a missing declaration, because
 * three of the four surfaces want it and only one states otherwise. */
.text {
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--survey-words-ink, var(--text-2));
  text-wrap: pretty;
}

/* ── photographs ────────────────────────────────────────────────────────────── */

.photos {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5px);
  align-items: flex-start;
}

/* One photograph under its pose's name — the report's own arrangement. The caption is part of the
 * frame rather than a line under the strip, so three poses read as three labelled frames. */
.pose {
  display: inline-flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-5px);
  align-items: center;
}

.poseName {
  font-size: var(--fs-micro-label);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-transform: uppercase;
  letter-spacing: var(--ls-lab);
}

/* The fit and the ground are `shared/ui/photoBox`'s; the box is stated here so the slot exists
 * before the fetch does, which is the whole of the "a photo holds its own box" rule. */
.photo {
  display: block;
  flex: none;
  width: var(--survey-photo-w);
  height: var(--survey-photo-h);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-7px);
}

/* ── an exercise clip ───────────────────────────────────────────────────────── */

.clip {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: var(--survey-clip-w);
  height: var(--survey-clip-h);
  overflow: hidden;
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-7px);
}

/* The fit and the ground are `shared/ui/photoBox`'s — the `<video>` wears `photoBox.photo` beside
 * this, exactly as the poster `<img>` does. What is left here is the box, which is the surface's
 * job: nothing is declared on both sides. */
.clipVideo {
  display: block;
  width: 100%;
  height: 100%;
}

.clipPlay {
  position: absolute;
  display: grid;
  place-items: center;
  width: var(--sz-survey-play);
  height: var(--sz-survey-play);
  color: var(--text);
  background: var(--scrim);
  border-radius: var(--r-circle);
}

.clipDuration {
  position: absolute;
  right: var(--space-5px);
  bottom: var(--space-5px);
  padding: var(--space-1px) var(--space-5px);
  font-size: var(--fs-micro-label);
  font-style: normal;
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text);
  background: var(--scrim);
  border-radius: var(--radius-4px);
  font-variant-numeric: tabular-nums;
}

/* A clip that aged out is a dashed frame carrying the reason, never a player that cannot play.
 * Clips expire on their own schedule and the trainer is told rather than shown a broken box. */

/* `--text-2`: this frame's whole content is the sentence in it, so the sentence has to be read. */
.clipExpired {
  padding: var(--space-2) var(--space-5px);
  font-size: var(--fs-tag);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-align: center;
  background: transparent;
  border-style: dashed;
  border-color: var(--border-2);
}
